import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../Firebase/firebase-config';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, update } from 'firebase/database';
import { getStorage, ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import './DeleteAccount.css';

const DeleteAccount = () => {
    const [confirmed, setConfirmed] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleConfirm = () => setConfirmed(true);

    // Recursive function to delete all files and subfolders in a folder
    const deleteFolderRecursive = async (folderRef) => {
        try {
            const listResult = await listAll(folderRef);

            // Delete all files in this folder
            await Promise.all(listResult.items.map(itemRef => deleteObject(itemRef)));

            // Recursively delete files in subfolders
            for (const prefix of listResult.prefixes) {
                await deleteFolderRecursive(prefix);
            }
        } catch (err) {
            console.warn(`Error deleting folder ${folderRef.fullPath}:`, err.message);
        }
    };

    const deleteUserStorage = async (uid) => {
        const storage = getStorage();
        const storageNodes = [
            'Finance',
            'POFiles',
            'PaymentFiles',
            'chatDocuments',
            'mtc',
            'pendingProducts',
            'refunds',
            'user-statements',
            'userDocuments'
        ];

        for (const node of storageNodes) {
            const folderRef = storageRef(storage, `${node}/${uid}`);
            await deleteFolderRecursive(folderRef);
        }
    };

    const handleDelete = async () => {
        try {
            const user = auth.currentUser;

            if (!password) {
                alert('Password is required to delete account.');
                return;
            }

            // Re-authenticate the user
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);

            const uid = user.uid;

            // Delete user data from Realtime Database
            const nodesToDelete = [
                'issues', 'machineryOrder', 'needHelp', 'Finance',
                'negotiations', 'new-code', 'newProducts', 'notes', 'offers', 'orders',
                'priceInformation', 'profileDashboard', 'queries', 'quoteRequests',
                'rates', 'rateRequest', 'refunds', 'regions', 'steelOrder', 'supplierContacts',
                'supplier_notifications', 'transport_goods', 'transport_goods_replies',
                'trendingProducts', 'userBrandAlternatives', 'userComparisonData',
                'user_notifications','userchatbox','groupChats','ActivePO','accessPermissions','alt_alarm','broadcasts',
                'feedback','loanRequest',
            ];

            const updates = {};
            nodesToDelete.forEach((node) => {
                updates[`${node}/${uid}`] = null;
            });

            await update(ref(db), updates);

            // Delete user files from Storage
            await deleteUserStorage(uid);

            // Delete user account
            await user.delete();

            // Redirect to landing page
            navigate('/');
        } catch (err) {
            console.error("Delete error:", err);
            setError('Error deleting account: ' + err.message);
        }
    };

    return (
        <div className="center-wrapper">
            <div className="delete-account-container">
                <h2>Delete Account</h2>

                {!confirmed ? (
                    <>
                        <p>
                            Click the <strong>"Yes, Delete Account"</strong> button below if you are sure you want to delete your account.
                            All your personal information and account details will be permanently removed. You will be logged out of your
                            account after the deletion process is completed.
                        </p>
                        <p>
                            If you change your mind, click <strong>"Cancel"</strong> to go back to your profile.
                        </p>
                        <p className="warning-text">⚠️ This action cannot be undone.</p>
                        <div className="button-group">
                            <button onClick={handleConfirm} className="delete-button">Yes, Delete Account</button>
                            <button onClick={() => navigate('/profile')} className="cancel-button">Cancel</button>
                        </div>
                    </>
                ) : (
                    <>
                        <label>Please re-enter your password to confirm deletion:</label>
                        <form onSubmit={(e) => { e.preventDefault(); handleDelete(); }} autoComplete="off">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="new-password"
                            />
                            <button type="submit" className="delete-button">Delete Account</button>
                        </form>
                        <button onClick={() => navigate('/profile')} className="cancel-button">Cancel</button>
                        {error && <p className="error">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
};

export default DeleteAccount;
